import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Zap, 
  Users, 
  MapPin, 
  Search,
  LogOut,
  ArrowRight,
  Locate
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom green icon for communities
const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// French cities coordinates for demo
const CITY_COORDS = {
  'paris': [48.8566, 2.3522],
  'lyon': [45.7640, 4.8357],
  'marseille': [43.2965, 5.3698],
  'toulouse': [43.6047, 1.4442],
  'nice': [43.7102, 7.2620],
  'nantes': [47.2184, -1.5536],
  'bordeaux': [44.8378, -0.5792],
  'lille': [50.6292, 3.0573],
  'strasbourg': [48.5734, 7.7521],
  'montpellier': [43.6108, 3.8767],
  'default': [46.603354, 1.888334] // France center
};

const getCityCoords = (location) => {
  if (!location) return CITY_COORDS.default;
  const loc = location.toLowerCase();
  for (const [city, coords] of Object.entries(CITY_COORDS)) {
    if (loc.includes(city)) return coords;
  }
  // Return random offset from France center for unknown cities
  return [
    CITY_COORDS.default[0] + (Math.random() - 0.5) * 4,
    CITY_COORDS.default[1] + (Math.random() - 0.5) * 6
  ];
};

const CommunityMap = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapCenter, setMapCenter] = useState(CITY_COORDS.default);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async (location = '') => {
    try {
      const params = location ? { location } : {};
      const response = await axios.get(`${API_URL}/api/communities`, { params });
      
      // Add coordinates to communities
      const communitiesWithCoords = response.data.map(comm => ({
        ...comm,
        coords: getCityCoords(comm.location)
      }));
      
      setCommunities(communitiesWithCoords);
    } catch (error) {
      console.error('Error fetching communities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery) {
      const coords = getCityCoords(searchQuery);
      setMapCenter(coords);
      fetchCommunities(searchQuery);
    }
  };

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = [position.coords.latitude, position.coords.longitude];
          setUserLocation(coords);
          setMapCenter(coords);
          toast.success('Position trouvée !');
        },
        (error) => {
          toast.error('Impossible de vous localiser');
        }
      );
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-[1000] glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link">Liste</Link>
              <Link to="/map" className="nav-link active">Carte</Link>
              <Link to="/simulator" className="nav-link">Simulateur</Link>
            </div>
            
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Button variant="ghost" size="icon" onClick={handleLogout}>
                  <LogOut className="w-5 h-5" />
                </Button>
              ) : (
                <Button onClick={() => navigate('/login')} className="btn-primary">
                  Connexion
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Search Bar */}
      <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[1000] w-full max-w-md px-4">
        <form onSubmit={handleSearch} className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Rechercher une ville..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-24 py-6 bg-white shadow-lg border-0 rounded-full"
          />
          <Button 
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full"
          >
            Chercher
          </Button>
        </form>
      </div>

      {/* Locate Me Button */}
      <Button
        onClick={handleLocateMe}
        className="fixed bottom-6 right-6 z-[1000] rounded-full w-14 h-14 shadow-lg"
        size="icon"
      >
        <Locate className="w-6 h-6" />
      </Button>

      {/* Map */}
      <div className="h-screen w-full pt-16">
        {!loading && (
          <MapContainer
            center={mapCenter}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* User location */}
            {userLocation && (
              <Circle
                center={userLocation}
                radius={2000}
                pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.2 }}
              />
            )}
            
            {/* Community markers */}
            {communities.map((community) => (
              <Marker
                key={community.id}
                position={community.coords}
                icon={greenIcon}
              >
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <h3 className="font-bold text-lg text-primary">{community.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 my-1">
                      <MapPin className="w-3 h-3" /> {community.location}
                    </p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Users className="w-3 h-3" /> {community.member_count} membres
                    </p>
                    <p className="text-sm font-medium text-primary mt-2">
                      {community.price_per_kwh} €/kWh
                    </p>
                    <Button
                      onClick={() => navigate(`/communities/${community.id}`)}
                      className="w-full mt-3 btn-primary text-sm"
                      size="sm"
                    >
                      Voir détails
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>

      {/* Stats Overlay */}
      <div className="fixed bottom-6 left-6 z-[1000]">
        <Card className="bg-white/90 backdrop-blur-sm shadow-lg">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Communautés actives</p>
            <p className="text-2xl font-bold text-primary">{communities.length}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CommunityMap;
